import { useEffect, useRef, useState, useCallback } from 'react';

import tgIcon from '../icons/TG.svg';
import whatsappIcon from '../icons/WA.svg';
import mangoIcon from '../icons/mango.svg';

import type { SupportLink } from '@Components/OnlineConsultation';

const useMangoChat = () => {
  const initedRef = useRef(false);
  const scriptRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [links, setLinks] = useState<SupportLink[]>(null);
  const [observer, setObserver] = useState<MutationObserver>(null);

  const setMangoElements = useCallback(
    async (elem: HTMLElement) => {
      elem.style.display = 'none';

      const elements = [
        { id: 'mgo-mcw-chat-button', name: 'mangochat', img: mangoIcon },
        { id: 'mgo-mcw-tg-button', name: 'telegram', img: tgIcon },
        { id: 'mgo-mcw-wa-button', name: 'whatsapp', img: whatsappIcon },
      ];

      const elLinks: SupportLink[] = elements.map(({ id, name, img }) => ({
        name,
        img,
        cb: async () => {
          const widget = document.getElementById('mgo-mcw-widget');

          if (!widget) return;

          const need2click =
            widget.classList.contains('mgo-mcw_state-default') &&
            widget.classList.contains('mgo-mcw-widget_mobile-view');

          const element = document.getElementById(id);

          if (!element) return;

          element.click();

          if (need2click) {
            await new Promise((resolve) => setTimeout(resolve, 300));
            element.click();
          }
        },
      }));

      setLinks(elLinks);
    },
    [setLinks],
  );

  const handleOnLoad = useCallback(async () => {
    const targetId = 'mgo-mcw-group-buttons';
    const obd = new MutationObserver(() => {
      if (document.getElementById(targetId)) {
        obd.disconnect();
        const elem = document.getElementById(targetId);

        setMangoElements(elem);
      }
    });

    obd.observe(document.body, {
      childList: true,
      subtree: true,
    });

    setObserver(obd);
    setIsLoaded(true);
  }, [setMangoElements, setObserver, setIsLoaded]);

  const handleOnError = useCallback(() => {
    setIsError(true);

    if (observer) {
      observer.disconnect();
    }
  }, [observer]);

  useEffect(() => {
    if (initedRef.current) return;

    initedRef.current = true;
    scriptRef.current = document.createElement('script');
    scriptRef.current.src = '/mango-chat-script.js';
    scriptRef.current.async = true;
    document.body.appendChild(scriptRef.current);
    scriptRef.current.onload = handleOnLoad;
    scriptRef.current.onerror = handleOnError;

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [observer, handleOnError, handleOnLoad]);

  return { isLoaded, isError, links };
};

export default useMangoChat;
